import { useQuery, UseQueryResult, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import axios from '@utils/axios';
import { CustomBlogPost } from '@api/database/models/customBlogPost';
import { dealsAction } from '@actions/index';
import { CustomBlogPostUser } from '@types';
import queryClient from '../queryClient';
import { useSnackbar } from './useSnackbar';

export function useGetCustomBlogPosts(): UseQueryResult<CustomBlogPostUser[]> {
  const queryFn = async (): Promise<CustomBlogPostUser[]> => {
    const result = await axios.get<{
      data: CustomBlogPostUser[];
    }>(`/api/get-blog-posts`);
    return result.data;
  };

  return useQuery<CustomBlogPostUser[], Error>(
    `custom-blog-posts`,
    () => queryFn(),
    {
      onError: () => {
        console.error(
          'Something went wrong while fetching the custom blog posts'
        );
      }
    }
  );
}

export function useAdminGetCustomBlogPosts(): UseQueryResult<CustomBlogPost[]> {
  const queryFn = async (): Promise<CustomBlogPost[]> => {
    const result = await axios.get<{
      data: CustomBlogPost[];
    }>(`/api/dashboard/get-admin-custom-blog-posts`);
    return result.data;
  };

  return useQuery<CustomBlogPost[], Error>(
    `admin-custom-blog-posts`,
    () => queryFn(),
    {
      onError: () => {
        console.error(
          'Something went wrong while fetching the custom blog posts'
        );
      }
    }
  );
}

export function useCreateEditCustomBlogPost() {
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();
  interface RequestData {
    id: number | null;
    title: string;
    slug: string;
    postText: string;
    isPinned: boolean;
    isLive: boolean;
    authorId: number;
    publishedAt: Date;
    commaSepASINsMentioned: string;
    description: string;
    seoImage: string;
    type: string;
    expiresAt?: Date | null;
  }

  const mutationFn = async ({
    id,
    title,
    slug,
    postText,
    isPinned,
    isLive,
    authorId,
    publishedAt,
    commaSepASINsMentioned,
    description,
    seoImage,
    type,
    expiresAt
  }: RequestData): Promise<void> => {
    return axios.post(`/api/dashboard/create-edit-blog-post`, {
      id,
      title,
      slug,
      postText,
      isPinned,
      isLive,
      authorId,
      publishedAt,
      commaSepASINsMentioned,
      description,
      seoImage,
      type,
      expiresAt
    });
  };

  return useMutation(mutationFn, {
    onSuccess: (_data, { id, slug }) => {
      const message = id
        ? 'Custom blog post updated'
        : 'Custom blog post created';
      showMessage(message);
      queryClient.invalidateQueries('admin-custom-blog-posts');
      if (id) {
        queryClient.invalidateQueries(`admin-custom-blog-post-${id}`);
      }
      // refresh the blog on save
      dispatch(dealsAction.getBlogSEO(slug));
    },
    onError: () => {
      showMessage('Error adding custom blog post');
    }
  });
}

export function useDeleteCustomBlogPost() {
  const { showMessage } = useSnackbar();
  interface RequestData {
    id: number;
  }

  const mutationFn = async ({ id }: RequestData): Promise<void> => {
    return axios.post(`/api/dashboard/delete-custom-blog-post`, {
      id
    });
  };

  return useMutation(mutationFn, {
    onSuccess: () => {
      showMessage('Custom blog post deleted');
      queryClient.invalidateQueries('admin-custom-blog-posts');
    },
    onError: () => {
      showMessage('Error deleting custom blog post');
    }
  });
}

export function useAdminGetCustomBlogPost(
  id?: number | string
): UseQueryResult<CustomBlogPost> {
  const queryFn = async (): Promise<CustomBlogPost> => {
    const result = await axios.get<{
      data: CustomBlogPost;
    }>(`/api/dashboard/get-admin-custom-blog-post/${id}`);
    return result.data;
  };

  return useQuery<CustomBlogPost, Error>(
    `admin-custom-blog-post-${id}`,
    () => queryFn(),
    {
      enabled: !!id,
      onError: () => {
        console.error(
          'Something went wrong while fetching the custom blog post'
        );
      }
    }
  );
}
